import { memo } from 'react';
import type { StackProps } from '@library';
import { VStack } from '@library';
import Divider from '../../../core/components/Divider';
import useProductDetailsLink from '../../hooks/useProductDetailsLink';
import useCustomerPriceVisibility from '../../../core/hooks/useCustomerPriceVisibility';
import CurrentStoreLink from '../../../core/components/router/CurrentStoreLink';
import useTabletView from '../../../core/hooks/dom/useTabletView';
import type {
    LoggedInProductCustomerPrice,
    ProductCustomerPrice,
    ProductWithRelatedData,
} from '../../types';
import ProductMyHEKPrice from './ProductMyHEKPrice';
import ProductRetailPrice from './ProductRetailPrice';
import styles from './ProductTile.module.scss';

const ProductTilePricing = ({
    loading = false,
    variantStartingPrice = false,
    customerPrice,
    product,
    selectedSKU: activeSKU,
    divider,
    gap = 'small',
}: {
    loading?: boolean;
    variantStartingPrice?: boolean;
    customerPrice?: ProductCustomerPrice;
    product: ProductWithRelatedData;
    selectedSKU?: string;
    divider: boolean;
    gap?: StackProps['gap'];
}) => {
    const { id, variants, onSale } = product;
    const [showCustomerPrice] = useCustomerPriceVisibility();
    const productDetailsLink = useProductDetailsLink(id, variants, activeSKU);

    const activeVariant = variants.find(variant => variant.sku === activeSKU);
    const isTabletView = useTabletView();

    return (
        <div className={styles.pricingContainer}>
            <VStack gap={gap}>
                <CurrentStoreLink to={productDetailsLink}>
                    <ProductRetailPrice
                        loading={loading}
                        retailPrice={customerPrice?.retailPrice}
                        isStartingPrice={
                            variantStartingPrice &&
                            !activeVariant &&
                            !isTabletView
                        }
                    />
                </CurrentStoreLink>
                {divider && <Divider lengthPercentage={100} />}
                {showCustomerPrice && (
                    <ProductMyHEKPrice
                        loading={loading}
                        pricingInfo={
                            customerPrice as
                                | LoggedInProductCustomerPrice
                                | undefined
                        }
                        isStartingPrice={
                            variantStartingPrice &&
                            !activeVariant &&
                            !isTabletView
                        }
                        onSale={onSale}
                        productWithAdditionalCharges={product}
                        topAlignPrices
                        product={activeVariant || product}
                    />
                )}
            </VStack>
        </div>
    );
};

export default memo(ProductTilePricing);
